import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter,
  Input, OnDestroy, Output, SimpleChanges, inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LocalizationPipe } from '@abp/ng.core';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { QueueDto } from '../../models/queue.dto';
import { QueueCardComponent } from '../queue-card/queue-card.component';
import { CrudService } from 'src/core/services/crud/crud.service';
import { ListRequestDto } from 'src/core/models/request/list-request.dto';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import { Subject, debounceTime, takeUntil } from 'rxjs';

type StatusFilter = 'all' | 'active' | 'inactive';

@Component({
  selector: 'app-queue-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LocalizationPipe,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    QueueCardComponent,
  ],
  templateUrl: './queue-list.component.html',
  styleUrl: './queue-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QueueListComponent implements OnDestroy {
  private readonly crudService = inject(CrudService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();
  private readonly searchChange$ = new Subject<string>();

  @Input() reloadVersion = 0;
  @Output() newQueue = new EventEmitter<void>();
  @Output() editQueue = new EventEmitter<QueueDto>();

  allQueues: QueueDto[] = [];
  loading = false;
  searchText = '';
  statusFilter: StatusFilter = 'all';

  pageSize = 6;
  currentPage = 0;

  get totalCount(): number {
    return this.allQueues.length;
  }

  get activeCount(): number {
    return this.allQueues.filter(q => q.isActive).length;
  }

  get inactiveCount(): number {
    return this.allQueues.filter(q => !q.isActive).length;
  }

  get filteredQueues(): QueueDto[] {
    if (this.statusFilter === 'active') return this.allQueues.filter(q => q.isActive);
    if (this.statusFilter === 'inactive') return this.allQueues.filter(q => !q.isActive);
    return this.allQueues;
  }

  get pagedQueues(): QueueDto[] {
    const start = this.currentPage * this.pageSize;
    return this.filteredQueues.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredQueues.length / this.pageSize);
  }

  get showPagination(): boolean {
    return this.totalPages > 1;
  }

  ngOnInit(): void {
    this.searchChange$.pipe(
      debounceTime(300),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.currentPage = 0;
      this.loadQueues();
    });

    this.loadQueues();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reloadVersion'] && !changes['reloadVersion'].firstChange) {
      this.loadQueues();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setStatusFilter(filter: StatusFilter): void {
    this.statusFilter = filter;
    this.currentPage = 0;
  }

  onSearchChange(): void {
    this.searchChange$.next(this.searchText);
  }

  goToPage(page: number): void {
    if (page >= 0 && page < this.totalPages) {
      this.currentPage = page;
    }
  }

  onCardClick(queue: QueueDto): void {
    this.editQueue.emit(queue);
  }

  trackByQueueId(_: number, queue: QueueDto): number | null {
    return queue.id;
  }

  private loadQueues(): void {
    this.loading = true;
    const request: ListRequestDto = {
      skipCount: 0,
      maxResultCount: 1000,
      filters: this.searchText.trim()
        ? [{ field: 'searchText', operator: 0, value: this.searchText.trim() }]
        : [],
      sorters: [
        { field: 'lastModificationTime', direction: 'desc' },
        { field: 'creationTime', direction: 'desc' },
      ],
    };
    this.crudService.get<QueueDto>(QueueDto, request).subscribe({
      next: (response: ListResponseDto<QueueDto>) => {
        const items = response.items ?? [];
        this.allQueues = items.map(q => {
          const raw = q as any;
          const assignedUsers: any[] = raw.assignedUsers ?? [];
          return Object.assign(new QueueDto(), q, { assignedUsers });
        });
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }
}
